import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DashboardLayout from "../../../components/DashboardLayout";
import Loader from "../../../components/Loader";
import { showErrorToastMsg } from "../../../utility/ToastMessage";
import { getCourseByIdAPI } from "../../../services/portal/student/studentCourseService";
import StudyPackCard from "../../../components/portal/StudyPackCard";
import { RoutePaths } from "../../../utility/RoutePaths";
import { formatDate } from "../../../utility/Common";
import VideoCard from "../../../components/portal/VideoCard";

const CourseDetailsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const courseId = searchParams.get("courseId");

  const [paymentStatus, setPaymentStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState(null);
  const [files, setFiles] = useState([]);
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    if (courseId) {
      loadData();
    }
  }, [courseId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await getCourseByIdAPI(courseId);
      if (res && res.IsSuccessful && res.Data) {
        setCourse(res.Data);
        if (res.Data.CourseDetails) {
          setFiles(
            res.Data.CourseDetails.filter((detail) => detail.Type === 1),
          );
          setVideos(
            res.Data.CourseDetails.filter((detail) => detail.Type === 2),
          );
        }
        setPaymentStatus(res.Data.PaymentStatus || "");
      }
    } catch (error) {
      console.error("Error loading course details:", error);
      showErrorToastMsg("Failed to load course");
    } finally {
      setLoading(false);
    }
  };

  if (!courseId) {
    return (
      <DashboardLayout title="Course Details" subtitle="">
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg">Invalid course link.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Course Details"
      subtitle={course ? course.Title : "Loading..."}
    >
      <div className="mb-4">
        <button
          type="button"
          onClick={() => navigate(RoutePaths.STUDENT_COURSES_PAGE)}
          className="text-sm font-medium text-primary-600 hover:text-primary-800"
        >
          ← Back to courses
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader />
        </div>
      ) : !course ? (
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg">Course not found.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Course Info */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {course.Image && (
              <img
                src={course.Image}
                alt={course.Title}
                className="w-full h-56 object-cover"
              />
            )}
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {course.Title}
              </h2>
              {course.Description && (
                <p className="text-gray-600 mt-2">{course.Description}</p>
              )}
              <div className="flex flex-wrap gap-6 mt-4">
                {course.Date && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <svg
                      className="w-4 h-4 text-primary-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <span className="font-medium">Date:</span>
                    {new Date(course.Date).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </div>
                )}
                {course.CourseFee !== undefined &&
                  course.CourseFee !== null && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <svg
                        className="w-4 h-4 text-primary-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span className="font-medium">Fee:</span>
                      <span className="font-semibold text-primary-600">
                        Rs. {course.CourseFee.toLocaleString()}
                      </span>
                    </div>
                  )}
              </div>
            </div>
          </div>

          {/* Payment Banner */}
          {course.StudentCourseAssignStatus === 0 && (
            <div className="bg-primary-600 text-white rounded-lg shadow-md p-6 mb-6 border-l-4">
              <div className="flex flex-col items-center justify-between">
                <p className="text-white">
                  පාඨමාලාවට සම්බන්ධ වීමට ගාස්තුව ගෙවන්න.
                </p>
                <h2 className="text-2xl font-bold text-white mt-4">
                  Rs. {course.CourseFee?.toLocaleString()}
                </h2>
                <button
                  onClick={() =>
                    navigate(
                      `${RoutePaths.STUDENT_COURSE_PAYMENT_PAGE}?courseId=${courseId}`,
                    )
                  }
                  className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium transition-colors mt-4"
                >
                  Pay
                </button>
              </div>
            </div>
          )}

          {/* Payment Pending / Rejected */}
          {course.StudentCourseAssignStatus === 1 && (
            <div className="bg-yellow-600 text-white rounded-lg shadow-md p-6 mb-6 border-l-4">
              <div className="flex flex-col justify-between">
                <p className="text-white">
                  Your payment record verification is pending. This will take
                  1-2 business hours. If you have any questions, please contact
                  support.
                </p>
                <p className="text-white mt-2">
                  ඔබගේ ගෙවීම් වාර්තා මේ වනවිට සත්‍යාපනය කරමින් පවතී. මෙයට පැය
                  1-2ක් ගතවනු ඇත. ඔබට කිසියම් ප්‍රශ්නයක් ඇත්නම්, කරුණාකර සහාය
                  අමතන්න.
                </p>
                {course.PaymentDate && (
                  <h2 className="text-lg font-bold text-white mt-4">
                    Payment Date: {formatDate(course.PaymentDate)}
                  </h2>
                )}
              </div>
            </div>
          )}

          {/* Video */}
          {paymentStatus === "Paid" && (
            <>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Videos</h3>
                {videos.length === 0 ? (
                  <p className="text-gray-500 text-sm">
                    No videos available for this course.
                  </p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {videos.map((video) => (
                      <VideoCard key={video.Id} video={video} />
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Files</h3>
                {files.length === 0 ? (
                  <p className="text-gray-500 text-sm">
                    No files available for this course.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-4">
                    {files.map((file) => (
                      <StudyPackCard key={file.Id} document={file} />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </DashboardLayout>
  );
};

export default CourseDetailsPage;
